import PageHeader from 'components/common/PageHeader';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createProject,
  getProjectById,
  setProjectActive,
  updateProject
} from 'redux/projectsSlice';
import messageHandler from 'utils/messageHandler';
import { useQuery } from 'utils/useQuery';
import ProjectWizardProvider from './ProjectWizardProvider';
import Steps from './Steps';
import { FooterWizard } from './footer/FooterWizard';
import { InfoWizard } from './info/InfoWizard';
import { NavWizard } from './nav/NavWizard';
import { getPageHeader } from './steps/data';

const ProjectWizard = ({ create }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { form, types } = useSelector(state => state.props);

  const query = useQuery();
  const queryStep = Number(query.get('step'));

  const yupSubmitRef = useRef(null);

  const [step, setStep] = useState(queryStep || 1);
  const [action, setAction] = useState('');

  const [data, setData] = useState();
  const [stepStatus, setStepStatus] = useState();

  const fetchData = async () => {
    if (create) {
      setData({});
      setStepStatus({});
    } else {
      const project = await dispatch(getProjectById(id));

      setData(project);
      setStepStatus(project);
    }
  };

  const handleSubmit = async (values, noChanges) => {
    setData({ ...data, ...values });

    const response = !noChanges && (await handleData({ ...data, ...values }));

    if (
      response.status > 399 ||
      (!data.units?.length && data.status === 'published')
    ) {
      return messageHandler(
        'error',
        response?.data?.message ||
          (!data.units?.length && data.status === 'published')
          ? '¡Atención! Debe agregar al menos una unidad para guardar el emprendimiento.'
          : '¡Atención! Ocurrió un error al intentar guardar la información, por favor intente nuevamente.'
      );
    }

    if ((noChanges || response) && action === 'next' && step !== 5) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setStep(step + 1);
    } else if ((noChanges || response) && action === 'next' && step === 5) {
      await handleActivate();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if ((noChanges || response) && action === 'exit') {
      messageHandler('success', response?.data?.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/emprendimientos');
    } else {
      messageHandler(
        'error',
        '¡Atención! Ocurrió un error al intentar guardar la información, por favor intente nuevamente.'
      );
    }
  };

  const handleData = async project => {
    const shareObj =
      // eslint-disable-next-line react/prop-types
      typeof project.share === 'string'
        ? // eslint-disable-next-line react/prop-types
          project.share
          ? {
              active: true,
              // eslint-disable-next-line react/prop-types
              percent: Number(project.share)
            }
          : {
              active: false,
              percent: 0
            }
        : // eslint-disable-next-line react/prop-types
          project.share;

    try {
      if (create) {
        if (step === 2) {
          const response = await handlePost({
            ...project,
            share: shareObj
          });

          if (response) {
            setData({
              ...data,
              ...project,
              _id: response,
              share: shareObj
            });
            return true;
          }
        }
        return true;
      }

      // eslint-disable-next-line no-unused-vars
      const { branch_office, real_estate, ...proj } = project;

      const response = await dispatch(
        updateProject(
          {
            ...proj,
            share: shareObj
          },
          id
        )
      );

      setData({
        ...proj,
        share: shareObj
      });

      return response;
    } catch (e) {
      return false;
    }
  };

  const handlePost = async project => {
    const id = await dispatch(createProject(project));

    if (id) {
      navigate(`/emprendimientos/editar/${id}?step=3`);
    }
  };

  const handleActivate = async () => {
    if (data.images?.length < 3) {
      messageHandler(
        'error',
        '¡Atención! Debe cargar al menos 3 fotos para activar la propiedad.'
      );
      return;
    }

    if (data.units?.length < 1) {
      messageHandler(
        'error',
        '¡Atención! Debe cargar al menos 1 unidad para activar la propiedad.'
      );
      return;
    }

    const response = await dispatch(setProjectActive(data._id));
    if (response < 400) {
      navigate('/emprendimientos');
      return response;
    }
  };

  const handleNavs = async targetStep => {
    if (!create) {
      window.scrollTo({ top: 50, behavior: 'smooth' });
      setStep(targetStep);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, create]);

  return (
    <ProjectWizardProvider
      handleSubmit={handleSubmit}
      yupSubmitRef={yupSubmitRef}
      step={step}
      setStep={setStep}
      data={data}
      setData={setData}
      stepStatus={stepStatus}
      setStepStatus={setStepStatus}
      setAction={setAction}
      handleNavs={handleNavs}
      status={data?.status}
      types={types}
      form={form}
    >
      <PageHeader {...getPageHeader(data, create)} className="mb-3 bg-white" />

      <Card noValidate className="theme-wizard bg-white">
        <Card.Header className="pb-0">
          <NavWizard />
        </Card.Header>

        <hr className="text-300" />

        {data && (
          <Card.Body className="px-4 py-0">
            <Steps />

            <hr className="text-400" />

            <InfoWizard />
          </Card.Body>
        )}

        <hr className="text-300" />

        <Card.Footer className="d-flex justify-content-center justify-content-md-start gap-2 px-4 pt-0">
          <FooterWizard />
        </Card.Footer>
      </Card>
    </ProjectWizardProvider>
  );
};

ProjectWizard.propTypes = {
  create: PropTypes.bool
};

export default ProjectWizard;
